import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import {
  AllocationRuleDto,
  AllocationRuleType,
  DeleteAllocationRuleRequest,
  UpsertAllocationRuleRequest,
} from '@yard/app/api'
import {
  mapAllocationRuleDtoToUpsertAllocationRuleRequest,
  mapFormDataToAllocationRuleTemplateUpsertRequest,
} from '@yard/components/AllocationRules/helpers/allocation-rule.mapper'
import { AllocationRuleFormData } from '@yard/components/AllocationRules/models/allocation-rule.model'
import { EventTypes } from '@yard/messages/eventsTypes'
import { DeleteResponse } from '@yard/messages/models/delete-response.model'
import { computed, makeObservable } from 'mobx'
import { allocationRuleService } from '../services/allocation-rule.service'
import { BaseEntityStore } from './base/BaseEntityStore'

export class AllocationRuleStore extends BaseEntityStore<AllocationRuleDto> {
  private isTemplateRulesFetched = false
  private isNonTemplateRulesFetched = false
  private readonly disposers: Array<() => void> = []

  constructor(private readonly messageBus: MessageBus) {
    super()

    makeObservable(this, {
      templateRules: computed,
      globalRules: computed,
      carrierVisitRules: computed,
      carrierVisitRulesById: computed,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<AllocationRuleDto>(
        EventTypes.AllocationRuleUpsertedEvent,
        this.handleUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<DeleteResponse>(
        EventTypes.AllocationRuleDeletedEvent,
        this.handleDeletedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async loadTemplateRules() {
    if (this.isTemplateRulesFetched) return
    try {
      const rules = await allocationRuleService.loadAllocationRules(true)
      this.upsertStoreItems(rules)
      this.isTemplateRulesFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async loadNonTemplateRules() {
    if (this.isNonTemplateRulesFetched) return
    try {
      const rules = await allocationRuleService.loadAllocationRules(false)
      this.upsertStoreItems(rules)
      this.isNonTemplateRulesFetched = true
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  async upsertTemplateAllocationRuleByAllocationRuleFormData(
    data: AllocationRuleFormData,
    id?: string,
  ) {
    return await allocationRuleService.upsertAllocationRuleTemplate(
      mapFormDataToAllocationRuleTemplateUpsertRequest(data, id),
    )
  }

  async upsertAllocationRule(data: UpsertAllocationRuleRequest) {
    return await allocationRuleService.upsertAllocationRule(data)
  }

  async upsertAllocationRuleByAllocationRuleDto(rule: AllocationRuleDto) {
    return await this.upsertAllocationRule(mapAllocationRuleDtoToUpsertAllocationRuleRequest(rule))
  }

  async deleteAllocationRule(data: DeleteAllocationRuleRequest) {
    await allocationRuleService.deleteAllocationRule(data)
  }

  async deleteGlobalAllocationRule(id: string) {
    await this.deleteAllocationRule({
      id,
      ruleType: AllocationRuleType.Global,
    })
  }

  async deleteTemplateAllocationRule(id: string) {
    await this.deleteAllocationRule({
      id,
      ruleType: AllocationRuleType.Template,
    })
  }

  async deleteCarrierVisitAllocationRule(id: string) {
    await this.deleteAllocationRule({
      id,
      ruleType: AllocationRuleType.CarrierVisit,
    })
  }

  get templateRules() {
    return this.items.filter(rule => rule.isTemplate)
  }

  get globalRules() {
    return this.items.filter(rule => !rule.isTemplate && !rule.facets?.carrierVisitId)
  }

  get carrierVisitRules() {
    return this.items.filter(rule => !rule.isTemplate && !!rule.facets?.carrierVisitId)
  }

  get carrierVisitRulesById() {
    return this.carrierVisitRules.reduce<Record<string, AllocationRuleDto[]>>((acc, rule) => {
      const cvId = rule.facets.carrierVisitId!
      acc[cvId] = acc[cvId] || []
      acc[cvId].push(rule)
      return acc
    }, {})
  }

  private handleUpsertedEvent = (event: IEvent<AllocationRuleDto>) => {
    const response = event.payload
    if (!response) return

    this.updateStoreItem(response, response.id)
  }

  private handleDeletedEvent = (event: IEvent<DeleteResponse>) => {
    const response = event.payload
    if (!response) return
    this.deleteStoreItem(response.id)
  }
}
